import { Component } from '@theme/component';
import {
  ThemeEvents,
  QuantitySelectorUpdateEvent,
} from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent,
} from '@shopify/events';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} [productImage]
 */

/**
 * Sticky add-to-cart for Vanero.
 *
 * The bar:
 * - appears when the normal product add-to-cart button has moved above the viewport;
 * - disappears when the normal button returns into view;
 * - works with the custom Vanero volume/bundle button;
 * - works on desktop and mobile;
 * - keeps variant and quantity updates from the theme;
 * - redirects to the cart after a successful sticky add.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {MutationObserver | null} */
  #pageObserver = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #stickyAddInProgress = false;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {number | undefined} */
  #reconnectTimeout;

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    this.#getInitialQuantity();
    this.#connectTargetButton();
    this.#setupPageObserver();

    const eventRoot = this.closest('.shopify-section') || document;

    eventRoot.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartError,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    window.addEventListener(
      'scroll',
      this.#requestVisibilityUpdate,
      {
        signal,
        passive: true,
      }
    );

    window.addEventListener(
      'resize',
      this.#handleResize,
      {
        signal,
        passive: true,
      }
    );

    document.addEventListener(
      'shopify:section:load',
      this.#handleSectionLoad,
      { signal }
    );

    /*
     * Product and app blocks may be rendered shortly after page load.
     */
    window.setTimeout(() => {
      if (signal.aborted) return;

      this.#connectTargetButton();
      this.#updateVisibility();
    }, 300);

    window.setTimeout(() => {
      if (signal.aborted) return;

      this.#connectTargetButton();
      this.#updateVisibility();
    }, 1000);

    window.setTimeout(() => {
      if (signal.aborted) return;

      this.#connectTargetButton();
      this.#updateVisibility();
    }, 2500);

    /*
     * Hide the bar if Shopify Inbox is active.
     */
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      if (this.#isChatActive()) {
        this.#hideStickyBar();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#pageObserver?.disconnect();

    if (this.#scrollFrame !== null) {
      window.cancelAnimationFrame(this.#scrollFrame);
    }

    if (this.#reconnectTimeout) {
      window.clearTimeout(this.#reconnectTimeout);
    }

    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Public click action used by the sticky Liquid button.
   */
  handleAddToCartClick = async () => {
    this.#connectTargetButton();

    const targetButton = this.#targetAddToCartButton;
    const stickyButton = this.refs.addToCartButton;

    if (!targetButton) {
      console.warn(
        '[sticky-add-to-cart] Normal Vanero add-to-cart button not found.'
      );

      return;
    }

    const unavailable =
      targetButton.disabled ||
      targetButton.getAttribute('aria-disabled') === 'true';

    if (unavailable) {
      stickyButton.disabled = true;
      stickyButton.setAttribute('aria-disabled', 'true');
      return;
    }

    this.#stickyAddInProgress = true;

    targetButton.dataset.puppet = 'true';

    stickyButton.dataset.loading = 'true';
    stickyButton.setAttribute('aria-busy', 'true');
    stickyButton.disabled = true;

    /*
     * Clicking the original button preserves all existing theme,
     * bundle, variant and validation logic.
     */
    targetButton.click();

    await this.#runFlyToCartAnimation();
  };

  /**
   * Finds and stores the real product-page add-to-cart button.
   */
  #connectTargetButton() {
    const foundButton = this.#findMainAddToCartButton();

    if (foundButton) {
      this.#targetAddToCartButton = foundButton;
    } else {
      this.#targetAddToCartButton = null;
    }

    this.#syncStickyButtonState();
    this.#updateVisibility();
  }

  /**
   * Finds the actual visible Vanero add-to-cart button.
   *
   * @returns {HTMLButtonElement | null}
   */
  #findMainAddToCartButton() {
    const productId = this.dataset.productId || '';

    const selectors = [
      /*
       * Vanero custom bundle/volume add-to-cart.
       */
      '.vb-volume__button',
      '.vb-volume button[type="submit"]',

      /*
       * Horizon theme product buttons.
       */
      productId
        ? `product-form-component[data-product-id="${CSS.escape(
            productId
          )}"] [ref="addToCartButton"]`
        : 'product-form-component [ref="addToCartButton"]',

      '.buy-buttons-block [ref="addToCartButton"]',
      '.buy-buttons-block button[name="add"]',
      '.buy-buttons-block button[type="submit"]',

      /*
       * Standard Shopify product buttons.
       */
      '.product-form__submit',
      'form[action*="/cart/add"] button[name="add"]',
      'form[action*="/cart/add"] button[type="submit"]',
      'button[data-add-to-cart]',
      '[data-add-to-cart]',
    ];

    for (const selector of selectors) {
      const candidates = Array.from(
        document.querySelectorAll(selector)
      );

      for (const candidate of candidates) {
        if (!(candidate instanceof HTMLButtonElement)) {
          continue;
        }

        if (this.contains(candidate)) {
          continue;
        }

        if (!this.#isUsableElement(candidate)) {
          continue;
        }

        if (this.#hasAddToCartText(candidate)) {
          return candidate;
        }
      }
    }

    /*
     * Last fallback: inspect visible buttons by their Danish text.
     */
    const allButtons = Array.from(
      document.querySelectorAll(
        'button, input[type="submit"], [role="button"]'
      )
    );

    for (const candidate of allButtons) {
      if (!(candidate instanceof HTMLButtonElement)) {
        continue;
      }

      if (this.contains(candidate)) {
        continue;
      }

      if (
        this.#isUsableElement(candidate) &&
        this.#hasAddToCartText(candidate)
      ) {
        return candidate;
      }
    }

    return null;
  }

  /**
   * Checks if an element is rendered and suitable as the trigger.
   *
   * @param {Element} element
   * @returns {boolean}
   */
  #isUsableElement(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    if (!document.documentElement.contains(element)) {
      return false;
    }

    if (
      element.closest(
        'cart-drawer, cart-notification, dialog, footer, product-card'
      )
    ) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 80 &&
      rect.height > 28 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  }

  /**
   * Checks the visible button text.
   *
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #hasAddToCartText(element) {
    const text = (
      element instanceof HTMLInputElement
        ? element.value
        : element.textContent
    )
      ?.replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    return Boolean(
      text?.includes('læg i kurv') ||
      text?.includes('tilføj til kurv') ||
      text?.includes('add to cart')
    );
  }

  /**
   * Shows or hides the bar based on the real button position.
   */
  #updateVisibility() {
    if (
      !this.#targetAddToCartButton ||
      !document.documentElement.contains(
        this.#targetAddToCartButton
      )
    ) {
      this.#targetAddToCartButton =
        this.#findMainAddToCartButton();
    }

    const targetButton = this.#targetAddToCartButton;

    if (!targetButton) {
      this.#hideStickyBar();
      return;
    }

    const rect = targetButton.getBoundingClientRect();

    /*
     * Show only after the normal add-to-cart button has moved
     * completely above the viewport.
     *
     * When the button is still below the viewport, the sticky stays hidden.
     */
    const buttonHasPassedAboveViewport = rect.bottom <= 0;

    if (
      buttonHasPassedAboveViewport &&
      !this.#isChatActive()
    ) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }

    this.#syncStickyButtonState();
  }

  /**
   * Limits scroll checks to one per animation frame.
   */
  #requestVisibilityUpdate = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame =
      window.requestAnimationFrame(() => {
        this.#scrollFrame = null;

        /*
         * Search again on every scroll frame because Shopify or
         * the bundle section may replace the button element.
         */
        const latestButton =
          this.#findMainAddToCartButton();

        if (latestButton) {
          this.#targetAddToCartButton = latestButton;
        }

        this.#updateVisibility();
      });
  };

  /**
   * Reconnects after a viewport change.
   */
  #handleResize = () => {
    this.#connectTargetButton();
    this.#requestVisibilityUpdate();
  };

  /**
   * Reconnects when Shopify reloads a section in the editor.
   */
  #handleSectionLoad = () => {
    this.#connectTargetButton();
    this.#updateVisibility();
  };

  /**
   * Watches for product and app DOM replacements.
   */
  #setupPageObserver() {
    this.#pageObserver?.disconnect();

    this.#pageObserver = new MutationObserver(() => {
      if (this.#reconnectTimeout) {
        window.clearTimeout(this.#reconnectTimeout);
      }

      this.#reconnectTimeout =
        window.setTimeout(() => {
          this.#connectTargetButton();
          this.#updateVisibility();
        }, 100);
    });

    this.#pageObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'disabled',
        'aria-disabled',
        'class',
      ],
    });
  }

  /**
   * Handles a Shopify product/variant selection.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId =
        String(optionValueId);
    }

    if (!(event.promise instanceof Promise)) {
      this.#connectTargetButton();
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          String(productId) !==
            String(this.dataset.productId)
        ) {
          return;
        }

        const newStickyComponent =
          /** @type {HTMLElement | null} */ (
            html.querySelector('sticky-add-to-cart')
          );

        const newStickyBar =
          newStickyComponent?.querySelector(
            '[ref="stickyBar"]'
          );

        if (newStickyBar) {
          const currentStuck =
            this.refs.stickyBar.dataset.stuck ??
            'false';

          morph(
            this.refs.stickyBar,
            newStickyBar,
            {
              childrenOnly: true,
            }
          );

          this.refs.stickyBar.dataset.stuck =
            currentStuck;

          if (
            newStickyComponent.dataset
              .variantAvailable != null
          ) {
            this.dataset.variantAvailable =
              newStickyComponent.dataset.variantAvailable;
          }
        }

        if (variant?.id) {
          this.dataset.currentVariantId =
            String(variant.id);
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#connectTargetButton();
        this.#updateButtonText();
        this.#syncStickyButtonState();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Variant update failed:',
            error
          );
        }
      });
  };

  /**
   * Displays the selected option title when no valid variant exists.
   */
  #handleVariantUnavailable() {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );

    const productId = this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${CSS.escape(
          productId || ''
        )}"]`
      );

    if (!variantTitleElement || !variantPicker) {
      return;
    }

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll(
        'input:checked'
      )
    )
      .map(
        (option) =>
          /** @type {HTMLInputElement} */ (
            option
          ).value
      )
      .filter(Boolean)
      .join(' / ');

    if (selectedOptions) {
      variantTitleElement.textContent =
        selectedOptions;
    }
  }

  /**
   * Handles a successful cart update.
   *
   * Redirects only when the sticky button initiated the add.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (!this.#stickyAddInProgress) return;

    const finish = () => {
      this.#resetStickyAction();

      const root =
        window.Shopify?.routes?.root || '/';

      window.location.assign(`${root}cart`);
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise
        .then(finish)
        .catch(() => {
          this.#resetStickyAction();
        });

      return;
    }

    finish();
  };

  /**
   * Handles cart errors.
   *
   * @param {CartErrorEvent} _event
   */
  #handleCartError = (_event) => {
    if (!this.#stickyAddInProgress) return;

    this.#resetStickyAction();
  };

  /**
   * Resets temporary loading and puppet states.
   */
  #resetStickyAction() {
    this.#stickyAddInProgress = false;

    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet =
        'false';
    }

    const stickyButton = this.refs.addToCartButton;

    stickyButton.removeAttribute('data-loading');
    stickyButton.removeAttribute('aria-busy');

    this.#syncStickyButtonState();
  }

  /**
   * Handles product-page quantity changes.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity =
      Number(event.detail.quantity) || 1;

    this.#updateButtonText();
  };

  /**
   * Runs the optional product-image-to-cart animation.
   */
  async #runFlyToCartAnimation() {
    const cartIcon = document.querySelector(
      '.header-actions__cart-icon, ' +
      '#cart-icon-bubble, ' +
      '[data-cart-icon]'
    );

    const productImage = this.refs.productImage;
    const stickyButton = this.refs.addToCartButton;

    if (
      !cartIcon ||
      !productImage ||
      !productImage.src
    ) {
      return;
    }

    stickyButton.dataset.added = 'true';

    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement =
      /** @type {FlyToCart} */ (
        document.createElement('fly-to-cart')
      );

    flyToCartElement.classList.add(
      'fly-to-cart--sticky'
    );

    flyToCartElement.style.setProperty(
      'background-image',
      `url("${productImage.src}")`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(
      flyToCartElement
    );

    try {
      await onAnimationEnd([
        stickyButton,
        flyToCartElement,
      ]);
    } catch {
      // Do not block the cart request when animation is interrupted.
    }

    this.#resetTimeout =
      window.setTimeout(() => {
        stickyButton.removeAttribute(
          'data-added'
        );
      }, 800);
  }

  /**
   * Shows the sticky bar.
   */
  #showStickyBar() {
    if (this.#isStuck) return;

    this.#isStuck = true;

    this.refs.stickyBar.dataset.stuck =
      'true';

    this.setAttribute(
      'aria-hidden',
      'false'
    );
  }

  /**
   * Hides the sticky bar.
   */
  #hideStickyBar() {
    this.#isStuck = false;

    this.refs.stickyBar.dataset.stuck =
      'false';

    this.setAttribute(
      'aria-hidden',
      'true'
    );
  }

  /**
   * Synchronizes sticky button availability with the real button.
   */
  #syncStickyButtonState() {
    const stickyButton =
      this.refs.addToCartButton;

    const targetButton =
      this.#targetAddToCartButton;

    if (!targetButton) {
      stickyButton.disabled = true;
      stickyButton.setAttribute(
        'aria-disabled',
        'true'
      );

      return;
    }

    const unavailable =
      targetButton.disabled ||
      targetButton.getAttribute(
        'aria-disabled'
      ) === 'true';

    if (!this.#stickyAddInProgress) {
      stickyButton.disabled = unavailable;
    }

    stickyButton.setAttribute(
      'aria-disabled',
      unavailable ? 'true' : 'false'
    );

    this.#updateButtonText();
  }

  /**
   * Checks whether Shopify Chat is active.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) {
      return false;
    }

    return Boolean(
      document.querySelector('shopify-chat')
    );
  }

  /**
   * Reads the initial quantity.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      Number.parseInt(
        this.dataset.initialQuantity || '1',
        10
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * Updates the quantity text in the sticky button.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    quantityNumber.textContent =
      String(this.#currentQuantity);

    const available =
      !addToCartButton.disabled &&
      addToCartButton.getAttribute(
        'aria-disabled'
      ) !== 'true';

    quantityDisplay.style.display =
      available && this.#currentQuantity > 1
        ? 'inline'
        : 'none';
  }
}

if (
  !customElements.get('sticky-add-to-cart')
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}